import React from "react";
import ReactDOM from "react-dom/client";
import { HelmetProvider } from 'react-helmet-async';
import "@/index.css";
import App from "@/App";

// ============== SERVICE WORKER COM ATUALIZAÇÃO RÁPIDA ==============

const SW_UPDATE_INTERVAL = 30000; // Verificar a cada 30 segundos
const APP_VERSION = '5.1.0'; // PIX Registration + Force Update

// Função para verificar e aplicar atualizações do SW
async function checkForUpdates(registration) {
  try {
    await registration.update();
    console.log('[App] Verificando atualizações do Service Worker...');
  } catch (error) {
    console.log('[App] Erro ao verificar atualizações:', error);
  }
}

// Função para forçar atualização
function forceRefresh() {
  if ('caches' in window) {
    caches.keys().then(names => {
      names.forEach(name => {
        if (name.startsWith('lyberhot-')) {
          caches.delete(name);
        }
      });
    });
  }
  window.location.reload(true);
}

// Registrar Service Worker
if ('serviceWorker' in navigator) {
  window.addEventListener('load', async () => {
    try {
      const registration = await navigator.serviceWorker.register('/sw.js', {
        updateViaCache: 'none' // Nunca usar cache para o SW
      });
      
      console.log('✅ Service Worker registrado:', registration.scope);
      
      // Verificar atualizações imediatamente
      checkForUpdates(registration);
      
      // Verificar atualizações periodicamente
      setInterval(() => checkForUpdates(registration), SW_UPDATE_INTERVAL);
      
      // Detectar nova versão disponível
      registration.addEventListener('updatefound', () => {
        const newWorker = registration.installing;
        console.log('🔄 Nova versão do Service Worker encontrada');
        
        if (newWorker) {
          newWorker.addEventListener('statechange', () => {
            if (newWorker.state === 'installed') {
              if (navigator.serviceWorker.controller) {
                // Nova versão pronta, mostrar notificação e recarregar
                console.log('🆕 Nova versão disponível!');
                
                // Forçar skip waiting
                newWorker.postMessage({ type: 'SKIP_WAITING' });
                
                // Recarregar após pequeno delay
                setTimeout(() => {
                  window.location.reload();
                }, 500);
              } else {
                // Primeira instalação
                console.log('✅ Service Worker instalado pela primeira vez');
              }
            }
          });
        }
      });
      
      // Escutar quando o controller mudar (nova versão ativada)
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        console.log('🔄 Service Worker atualizado!');
        // Recarregar para usar nova versão
        window.location.reload();
      });
      
      // Escutar mensagens do Service Worker
      navigator.serviceWorker.addEventListener('message', (event) => {
        if (event.data?.type === 'SW_ACTIVATED') {
          console.log(`[App] SW versão ${event.data.version} ativado`);
        }
      });
      
    } catch (error) {
      console.error('❌ Erro ao registrar Service Worker:', error);
    }
  });
}

// ============== PULL TO REFRESH (Mobile) ==============

let touchStartY = 0;
let touchEndY = 0;
let isPulling = false;

document.addEventListener('touchstart', (e) => {
  touchStartY = e.touches[0].clientY;
}, { passive: true });

document.addEventListener('touchmove', (e) => {
  if (window.scrollY === 0) {
    touchEndY = e.touches[0].clientY;
    const diff = touchEndY - touchStartY;
    
    if (diff > 100 && !isPulling) {
      isPulling = true;
    }
  }
}, { passive: true });

document.addEventListener('touchend', () => {
  if (isPulling && window.scrollY === 0) {
    console.log('[App] Pull to refresh ativado');
    forceRefresh();
  }
  isPulling = false;
  touchStartY = 0;
  touchEndY = 0;
}, { passive: true });

// ============== VISIBILITY CHANGE (Quando app volta ao foco) ==============

document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'visible') {
    console.log('[App] App voltou ao foco, verificando atualizações...');
    
    // Verificar atualizações do SW quando o app volta ao foco
    if (navigator.serviceWorker?.controller) {
      navigator.serviceWorker.ready.then(registration => {
        checkForUpdates(registration);
      });
    }
    
    // Disparar evento customizado para componentes React refetch
    window.dispatchEvent(new CustomEvent('app-visible'));
  }
});

// ============== ONLINE/OFFLINE STATUS ==============

window.addEventListener('online', () => {
  console.log('[App] Conexão restaurada');
  window.dispatchEvent(new CustomEvent('app-online'));
});

window.addEventListener('offline', () => {
  console.log('[App] Conexão perdida');
  window.dispatchEvent(new CustomEvent('app-offline'));
});

// ============== VERSÃO DO APP ==============

// Salvar versão do app para comparação
const savedVersion = localStorage.getItem('app_version');
if (savedVersion !== APP_VERSION) {
  console.log(`[App] Nova versão do app detectada: ${savedVersion} -> ${APP_VERSION}`);
  localStorage.setItem('app_version', APP_VERSION);
  
  // Limpar caches antigos
  if ('caches' in window) {
    caches.keys().then(names => {
      names.forEach(name => {
        if (name.startsWith('lyberhot-')) {
          caches.delete(name);
          console.log('[App] Cache antigo removido:', name);
        }
      });
    });
  }
}

// ============== RENDER DO APP ==============

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <HelmetProvider>
      <App />
    </HelmetProvider>
  </React.StrictMode>,
);

// Exportar função de refresh para uso em componentes
window.forceAppRefresh = forceRefresh;
